<template>
    <div :class="wrapperClasses" :style="wrapperStyles">
        <div class="label-desc">
            <div class="label" :for="name" v-if="label" :style="labelStyles">
                <span class="inner">{{ label }}</span>
                <span class="required" v-if="required">*</span>
            </div>
            <div
                class="extra-info"
                v-if="extraInfo"
                @click="$emit('extra-info-click')"
                :style="extraInfoStyles"
            >
                <i class="far fa-info-circle"></i>
                <span v-if="typeof extraInfo === 'string'" class="extra-info-title">{{ extraInfo }}</span>
            </div>
            <div class="description" v-if="description">{{ description }}</div>
        </div>
        <div class="input-wrapper">
            <div v-if="prefixInternal" class="fixed group-left">{{ prefixInternal }}</div>
            <input
                :type="type"
                :class="classes"
                :ref="name"
                :name="name"
                :id="id"
                :style="inputStyles"
                :disabled="disabled"
                :readonly="readonly"
                :value="value"
                @input="onInputMask"
                @change="onChangeTrim"
                @keydown="onKeydown"
                @focus="onFocus"
                @blur="onBlur"
                v-imask="imaskInternal"
                @accept="onAccept"
                @complete="onComplete"
                :placeholder="placeholder"
                :aria-label="description || label"
                :autocomplete="autocomplete"
                :min="min"
                :max="max"
            />
            <div v-if="suffix" class="fixed group-right">{{ suffix }}</div>
        </div>
    </div>
</template>

<script>
    import Autofocus from '../mixins/Autofocus.vue';
    import FormControl from '../mixins/FormControl.vue';
    import { IMaskDirective } from 'vue-imask';

    export default {
        name: 'FormInput',
        data() {
            return {};
        },
        mixins: [Autofocus, FormControl],
        props: {
            imask: Object,
            prefix: [String, Number],
            suffix: [String, Number],
            inputClasses: {
                type: Object,
                default: () => {
                    return {};
                },
            },
            placeholder: {
                type: String,
                default: ''
            },
            autocomplete: String,
            trim: {
                type: Boolean,
                default: false
            },
            align: {
                type: String,
                default: 'left'
            },
            currency: {
                type: Boolean,
                default: false
            },
            type: {
                type: String,
                default: 'text'
            },
            min: {
                type: [String, Number],
            },
            max: {
                type: [String, Number],
            },
        },
        computed: {
            classes() {
                return {
                    'group-right': !!this.prefixInternal,
                    'group-left': !!this.suffix,
                    'has-prefix-suffix': this.prefixInternal || this.suffix,
                    'align-right': this.align === 'right',
                    'align-center': this.align === 'center',
                    ...this.inputClasses,
                };
            },
            prefixInternal() {
                if (this.prefix) return this.prefix;
                if (this.currency) return '$';
                return null;
            },
            imaskInternal() {
                if (this.imask) return this.imask;
                if (this.currency) {
                    return {
                        mask: Number,
                        scale: 2,
                        thousandsSeparator: ',',
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        radix: '.',
                        min: 0,
                        max: 9999999
                    };
                }
                return null;
            }
        },
        directives: {
            imask: IMaskDirective,
        },
        methods: {
            async onInputMask(e) {
                if (!this.imask) {
                    this.onInput(e);
                }
            },
            onAccept(e) {
                this.onInput(e);
                this.$emit('accept', e.detail);
            },
            onComplete(e) {
                this.$emit('complete', e.detail);
            },
            onChangeTrim(e) {
                if (this.trim) {
                    let trimmed = e.target.value.trim();
                    if (e.target.value !== trimmed) {
                        e.target.value = trimmed;
                        this.onInput(e);
                    }
                }
                this.onChange(e);
            }
        },
    };
</script>

<style lang="scss" scoped>
    @import '../scss/formControl';

    .control-wrapper {
        .label {
            transform: translate3d($padding-left, 2em, 0.01px);
            color: $color-label;
            pointer-events: none;
        }

        &.filled .label,
        &:focus-within .label,
        &.horizontal .label {
            transform: translate3d(0, 0, 0.01px);
            color: $color-label;
        }
    }

    input {
        flex-grow: 1;
        font-size: 100%;
        padding-left: $padding-left;

        &.has-prefix-suffix {
            min-width: 0;
        }

        &.align-right {
            text-align: right;
        }

        &.align-center {
            text-align: center;
        }
    }

    .input-wrapper {
        display: flex;

        .group-right {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left: 0;
        }

        .group-left {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    .login-input {
        input {
            padding: 8px 12px;
            height: auto;
            border-radius: 4px;
        }
    }

    .fixed {
        background-color: $color-disabled-bg;
        color: $color-disabled-text;
        @include border;
        @include border-radius-small;
        display: flex;
        align-items: center;
        padding: 0 0.6em;
    }
</style>
